import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Skeleton from '@/common/components/skeleton/Skeleton';
import {
	bindVideoReadyListeners,
	isVideoElementReady,
	playVideoSafely,
} from '@/common/functions/videoElement';
import useStoryFileLoader from '../../hooks/useStoryFileLoader';
import { getMediaDirectSrc } from '../../utils/getMediaFileId';
import { inferStoryMediaType } from '../../utils/inferStoryMediaType';
import { StoryMedia, StoryMediaSkeleton } from '../../StoryViewer.styles';

const StoryViewerMedia = ({
	type,
	media,
	person,
	mediaId,
	slideKey,
	position,
	scale,
	poster,
	videoRef,
	storyPausedRef,
	canPlay = false,
	onMediaReady,
	onVideoProgress,
	onVideoEnded,
}) => {
	const directSrc = getMediaDirectSrc(media);
	const expectedMediaType = type === 'video' ? 'video' : 'image';
	const { fileSrc, contentType, isLoading } = useStoryFileLoader(
		mediaId,
		expectedMediaType,
	);
	const resolvedSrc = fileSrc || directSrc;
	const isFileReady = !mediaId || !isLoading;
	const isVideoSlide =
		type === 'video' ||
		inferStoryMediaType(media, person, contentType) === 'video' ||
		inferStoryMediaType({ base64: directSrc }, person) === 'video';
	const [isMediaLoaded, setIsMediaLoaded] = useState(false);
	const reportedReadyRef = useRef(false);
	const hasStartedPlaybackRef = useRef(false);
	const showSkeleton =
		Boolean(mediaId) && (!resolvedSrc || !isFileReady || !isMediaLoaded);

	const reportReady = () => {
		if (reportedReadyRef.current || !onMediaReady) {
			return;
		}

		reportedReadyRef.current = true;
		onMediaReady(slideKey, { isVideo: isVideoSlide });
	};

	useEffect(() => {
		reportedReadyRef.current = false;
		hasStartedPlaybackRef.current = false;
		setIsMediaLoaded(false);
	}, [slideKey, mediaId]);

	useEffect(() => {
		if (!mediaId && !directSrc) {
			reportReady();
		}
	}, [mediaId, directSrc, slideKey]);

	useLayoutEffect(() => {
		if (!isFileReady || !isMediaLoaded) {
			return;
		}

		reportReady();
	}, [isFileReady, isMediaLoaded, slideKey, isVideoSlide]);

	const attemptVideoPlay = useCallback(
		({ resetPosition = false } = {}) => {
			const video = videoRef.current;

			if (!video || !canPlay || storyPausedRef.current) {
				return;
			}

			if (resetPosition) {
				video.currentTime = 0;
				hasStartedPlaybackRef.current = false;
			}

			if (hasStartedPlaybackRef.current && !video.paused) {
				return;
			}

			hasStartedPlaybackRef.current = true;
			playVideoSafely(video);
		},
		[canPlay, storyPausedRef, videoRef],
	);

	useEffect(() => {
		const video = videoRef.current;

		if (!video || !isVideoSlide || !resolvedSrc) {
			return;
		}

		video.currentTime = 0;
		hasStartedPlaybackRef.current = false;
	}, [isVideoSlide, resolvedSrc, slideKey, videoRef]);

	useEffect(() => {
		const video = videoRef.current;

		if (!video || !isVideoSlide || !resolvedSrc) {
			return;
		}

		if (!canPlay || storyPausedRef.current) {
			video.pause();
			return;
		}

		if (isVideoElementReady(video)) {
			setIsMediaLoaded(true);
			attemptVideoPlay();
			return;
		}

		const handleCanPlay = () => {
			setIsMediaLoaded(true);
			attemptVideoPlay();
		};

		video.addEventListener('canplay', handleCanPlay, { once: true });

		return () => {
			video.removeEventListener('canplay', handleCanPlay);
		};
	}, [
		attemptVideoPlay,
		canPlay,
		isVideoSlide,
		resolvedSrc,
		slideKey,
		storyPausedRef,
		videoRef,
	]);

	const handleImageLoad = () => {
		setIsMediaLoaded(true);
	};

	const handleMediaError = () => {
		setIsMediaLoaded(true);
	};

	const handleVideoReady = () => {
		setIsMediaLoaded(true);
	};

	const handleVideoCanPlay = () => {
		setIsMediaLoaded(true);

		if (canPlay && !storyPausedRef.current) {
			attemptVideoPlay();
		}
	};

	const handleVideoTimeUpdate = () => {
		const video = videoRef.current;

		if (!video || !onVideoProgress || !canPlay) {
			return;
		}

		const duration = video.duration;

		if (!Number.isFinite(duration) || duration <= 0) {
			return;
		}

		onVideoProgress(video.currentTime / duration);
	};

	const handleVideoEnded = () => {
		if (!canPlay) {
			return;
		}

		onVideoEnded?.();
	};

	const setImageRef = (element) => {
		if (!element) {
			return;
		}

		if (element.complete && element.naturalWidth > 0) {
			handleImageLoad();
		}
	};

	const setVideoRef = (element) => {
		videoRef.current = element;

		if (!element) {
			return;
		}

		element.setAttribute('draggable', 'false');
		element.setAttribute('playsinline', '');
		element.setAttribute('webkit-playsinline', '');

		if (isVideoElementReady(element)) {
			handleVideoReady();
		}
	};

	useLayoutEffect(() => {
		const video = videoRef.current;

		if (!video || !isVideoSlide || !resolvedSrc) {
			return undefined;
		}

		return bindVideoReadyListeners(video, handleVideoReady, handleMediaError);
	}, [isVideoSlide, resolvedSrc, slideKey]);

	if (!mediaId && !directSrc) {
		return <StoryMedia />;
	}

	return (
		<StoryMedia>
			{showSkeleton && (
				<StoryMediaSkeleton>
					<Skeleton
						width="100%"
						height="100%"
						radius="0"
						minWidth="100%"
						minHeight="100%"
					/>
				</StoryMediaSkeleton>
			)}
			{!isVideoSlide && resolvedSrc && (
				<img
					ref={setImageRef}
					src={resolvedSrc}
					alt=""
					draggable={false}
					onLoad={handleImageLoad}
					onError={handleMediaError}
					style={{
						objectPosition: position,
						transform: `scale(${scale})`,
					}}
				/>
			)}
			{isVideoSlide && resolvedSrc && (
				<video
					ref={setVideoRef}
					key={`${slideKey}-${resolvedSrc}`}
					src={resolvedSrc}
					poster={poster || undefined}
					muted
					playsInline
					preload="auto"
					onLoadedData={handleVideoReady}
					onLoadedMetadata={handleVideoReady}
					onCanPlay={handleVideoCanPlay}
					onTimeUpdate={handleVideoTimeUpdate}
					onEnded={handleVideoEnded}
					onError={handleMediaError}
					style={{
						objectPosition: position,
						transform: `scale(${scale})`,
					}}
				/>
			)}
		</StoryMedia>
	);
};

export default memo(StoryViewerMedia);
